import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { setSession, getToken } from "@/lib/auth";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function AdminLoginPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  // If already logged in (e.g. user pressed browser-back after logging in,
  // or opened /admin/login directly with a valid token in storage), skip
  // the form and go straight to the dashboard. This prevents the "every
  // time I press back I see the login again" UX bug.
  useEffect(() => {
    const token = getToken();
    if (token) {
      const next = searchParams.get("next");
      navigate(next && next.startsWith("/admin") ? next : "/admin/dashboard", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setSession(res.data.token, res.data.email, remember);
      // Deep link preservation: redirect to ?next=... if provided (admin URLs only)
      // Use `replace` so the login page is REMOVED from the browser history —
      // pressing browser-back from the dashboard won't bring the user back to
      // the login form.
      const next = searchParams.get("next");
      if (next && next.startsWith("/admin")) {
        navigate(next, { replace: true });
      } else {
        navigate("/admin/dashboard", { replace: true });
      }
    } catch (err) {
      // More precise error messages so the host knows exactly what happened:
      //  - if the backend responded with a detail (es. 401 "Credenziali non valide") → show it
      //  - if the server returned 5xx → "Server temporaneamente non disponibile"
      //  - if it's a network error (err.response undefined) → "Connessione fallita"
      //  - fallback → t.common.error
      let msg;
      if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      } else if (err.response?.status >= 500) {
        msg = "Server temporaneamente non disponibile. Riprova tra qualche secondo.";
      } else if (err.code === "ERR_NETWORK" || !err.response) {
        msg = "Connessione al server fallita. Controlla che la tua rete sia attiva e riprova.";
      } else {
        msg = t.common.error;
      }
      toast.error(msg);
      // Log the full error to console so the host can share details if needed
      console.error("[admin-login] failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1E3A5F] grid place-items-center px-5">
      <div className="bg-white rounded-2xl p-8 md:p-10 w-full max-w-md shadow-2xl" data-testid="admin-login-card">
        <div className="w-12 h-12 rounded-full bg-[#AC5D45]/10 flex items-center justify-center mb-5">
          <Lock className="w-5 h-5 text-[#AC5D45]" />
        </div>
        <h1 className="font-display text-3xl text-[#1E3A5F] mb-1">{t.admin.login_title}</h1>
        <p className="text-sm text-[#635F59] mb-6">{t.admin.login_subtitle}</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>{t.admin.email}</Label>
            <Input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" />
          </div>
          <div>
            <Label>{t.admin.password}</Label>
            <Input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id="remember-me"
              data-testid="admin-remember-me"
              checked={remember}
              onCheckedChange={(v) => setRemember(v === true)}
            />
            <Label htmlFor="remember-me" className="text-sm font-normal cursor-pointer text-[#635F59]">
              {t.admin.remember_me}
            </Label>
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full h-11"
          >
            {loading ? t.common.loading : t.admin.login}
          </Button>
        </form>
      </div>
    </div>
  );
}
